/**
 * Authentication hook
 * Handles SSO authentication state and flow
 */

import { useState, useEffect, useCallback } from 'react';
import {
  authClient,
  handleAuthError,
  AUTH_ERROR_EVENT,
  clearSession,
  getStoredSession,
  isSafeReturnToPath,
  getStoredReturnToPath,
  clearStoredReturnToPath,
  setAuthOrigin,
  isLeaving,
} from '@/lib/auth-client';
import { getDashboardUrl } from '@/components/theme-provider';
import { getHomeLink } from '@/lib/home-link';
import { withRetry } from '@/lib/retry';
import { sameScope } from '@/lib/session-scope';
import type { AuthSession } from '@/types/auth';

// Module-level state to share SSO exchange across strict mode double-mounts
let ssoExchangePromise: Promise<AuthSession> | null = null;
let ssoExchangeInProgress = false; // Flag to indicate exchange is happening

// Escalation state for the auth-error listener (module-level on purpose: it is
// app-global and must survive strict-mode remounts).
//
// A 401/403 is "forgiven" once when the session cookie still validates
// server-side (transient blip). A second auth error while that forgiveness is
// still fresh means the cookie is valid but the backend keeps denying access —
// e.g. the account was re-scoped to a brand it no longer owns. Revalidating can
// never fix that, so we escalate: log out (clear the dead cookie) and send the
// user to the dashboard, instead of forgiving forever and leaving the UI stuck.
// Re-entering Commerce from the dashboard mints a fresh code with the account's
// current scope, so a merely scope-changed session self-heals from there — and
// leaving the app makes a redirect loop impossible.
let handlingAuthError = false;
let forgivenAuthErrorAt = 0;
const AUTH_ERROR_ESCALATION_WINDOW_MS = 60_000;

export interface UseAuthReturn {
  session: AuthSession | null;
  loading: boolean;
  /** True only after auth initialization is fully complete (including consume if needed) */
  ready: boolean;
  error: Error | null;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false); // Only true after full auth init (including consume if needed)
  const [error, setError] = useState<Error | null>(null);

  // Listen for auth errors from API (401/403) — retry session check before showing error
  useEffect(() => {
    let cancelled = false;

    const handleAuthErrorEvent = async () => {
      // A burst of parallel 401/403s (several polls failing in the same tick)
      // fires this once per failed request — handle the first, drop the rest.
      if (handlingAuthError) return;
      handlingAuthError = true;
      try {
        console.log('Auth error event received, retrying session check...');
        try {
          // Wait briefly and retry — the backend may be restarting (deploy)
          await new Promise((r) => setTimeout(r, 2000));
          if (cancelled) return;
          // Snapshot the scope we were operating under BEFORE dropping the cache,
          // to compare against the freshly re-derived one below.
          const cached = getStoredSession();
          // Drop the local cache first: the server just rejected it, so revalidate
          // the cookie server-side instead of trusting the unexpired cached copy.
          clearSession();
          const retried = await authClient.getCurrentSession();
          if (cancelled) return;
          if (retried.authenticated) {
            // A scope change is a structural verdict from /sso/session, not a blip: the
            // account was re-scoped server-side, so skip the forgiveness round and escalate.
            // (There is no "access revoked" verdict any more — a commerce identity always
            // resolves a scope, whatever its channels or lines are, K5.)
            const rescoped = cached?.authenticated ? !sameScope(cached.scope, retried.scope) : false;
            if (!rescoped && Date.now() - forgivenAuthErrorAt > AUTH_ERROR_ESCALATION_WINDOW_MS) {
              console.log('Session still valid after retry, ignoring transient error');
              forgivenAuthErrorAt = Date.now();
              setSession(retried);
              return;
            }
            // Either the scope changed server-side, or this is the second auth error
            // right after a "session still valid" verdict: the cookie is fine but the
            // backend keeps denying access. Clear the dead cookie and hand the user to
            // the dashboard — the surface their account can actually use.
            console.log(
              rescoped
                ? 'Brand access scope changed server-side — logging out, returning to dashboard'
                : 'Session valid but access repeatedly denied — logging out, returning to dashboard'
            );
            await authClient.logout();
            if (cancelled || isLeaving()) return;
            // Home, not re-auth: a cashier-opened session goes back to the cashier root (never
            // /comercio, which would mint a code straight back into the same denial loop).
            const home = getHomeLink();
            if (home.kind === 'cashier') {
              window.location.replace(home.url);
              return;
            }
            if (home.url) {
              window.location.href = home.url;
              return;
            }
            // No dashboard URL configured — fall back to the re-auth redirect.
            setError(null);
            setSession({ authenticated: false });
            return;
          }
        } catch {
          // Revalidation failed (backend unreachable) — fall through to re-auth;
          // the dashboard mints a fresh code and the consume path retries transient failures.
        }
        if (cancelled) return;
        // Session genuinely expired/invalid: redirect to re-auth via AuthGuard's
        // unauthenticated branch instead of showing the connection-error screen.
        console.log('Auth error confirmed after retry, clearing session and re-authenticating');
        handleAuthError();
        setError(null);
        setSession({ authenticated: false });
      } finally {
        handlingAuthError = false;
      }
    };

    window.addEventListener(AUTH_ERROR_EVENT, handleAuthErrorEvent);
    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_ERROR_EVENT, handleAuthErrorEvent);
    };
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    let mounted = true;

    async function initAuth() {
      try {
        // Cross-app logout: Dashboard redirects here with ?action=logout
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') === 'logout') {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          // A dashboard logout ends any cashier-opened session in this tab too.
          setAuthOrigin(null);
          await authClient.logout();
          const dashboardUrl = getDashboardUrl();
          if (dashboardUrl) {
            window.location.href = `${dashboardUrl}/login`;
          }
          return;
        }

        // Check if we have a delegation code in the URL fragment OR if exchange is in progress
        const hasCode = window.location.hash.includes('code=');
        // Read BEFORE the exchange strips the hash.
        const hashParams = hasCode ? new URLSearchParams(window.location.hash.slice(1)) : null;
        // Optional deep link carried alongside the code (e.g. "/orders?order=<id>" from the
        // dashboard's payment → WhatsApp-order chip).
        const returnToRaw = hashParams?.get('return_to') ?? null;
        const hashReturnTo = isSafeReturnToPath(returnToRaw) ? returnToRaw : null;
        // Which app minted this code (`from=cashier`) — decides where back / logout / re-auth go.
        // Rewritten on every new code, so a dashboard-initiated one clears a stale cashier origin.
        if (hashParams) setAuthOrigin(hashParams.get('from'));
        // Fall back to the path stored by AuthGuard before it redirected to the dashboard's
        // /oauth/authorize (peeked, not consumed — strict-mode's second mount finishes the
        // exchange via the shared promise and still needs it; consumed on use below).
        const returnTo = hashReturnTo
            ?? ((hasCode || ssoExchangeInProgress) ? getStoredReturnToPath() : null);

        if (hasCode || ssoExchangeInProgress) {
          // CODE DETECTED OR EXCHANGE IN PROGRESS
          // CRITICAL: Clear any old session immediately to prevent stale data usage
          if (!ssoExchangeInProgress) {
            console.log('New SSO code detected, clearing old session...');
            clearSession();
          }

          setLoading(true);
          setError(null);
          // Ensure session state is null during consume to prevent any stale render
          setSession(null);

          // If SSO exchange is already in progress (strict mode double-mount), reuse the promise
          if (ssoExchangePromise) {
            console.log('SSO exchange already in progress, awaiting result...');
            try {
              const newSession = await ssoExchangePromise;
              if (mounted) {
                setSession(newSession);
                setError(null);
                console.log('SSO authentication successful (from shared promise)');
                if (returnTo) {
                  clearStoredReturnToPath();
                  window.location.replace(returnTo);
                }
              }
            } catch (err) {
              console.error('SSO exchange failed (shared):', err);
              if (mounted) {
                setError(err instanceof Error ? err : new Error('Authentication failed'));
                setSession({ authenticated: false });
              }
            } finally {
              if (mounted) {
                setLoading(false);
                setReady(true);
              }
            }
          } else {
            // Start new SSO exchange
            console.log('SSO code detected, exchanging for access token...');
            ssoExchangeInProgress = true;
            ssoExchangePromise = authClient.consumeSSOCode();

            try {
              const newSession = await ssoExchangePromise;
              if (mounted) {
                setSession(newSession);
                setError(null);
                console.log('SSO authentication successful');
                if (returnTo) {
                  clearStoredReturnToPath();
                  window.location.replace(returnTo);
                }
              }
            } catch (err) {
              // On error: show auth error screen
              console.error('SSO exchange failed:', err);
              if (mounted) {
                setError(err instanceof Error ? err : new Error('Authentication failed'));
                setSession({ authenticated: false });
              }
            } finally {
              if (mounted) {
                setLoading(false);
                setReady(true);
              }
              // Clear the exchange state after a short delay to allow strict mode second mount to use it
              setTimeout(() => {
                ssoExchangePromise = null;
                ssoExchangeInProgress = false;
              }, 100);
            }
          }
        } else {
          // NO CODE AND NO EXCHANGE IN PROGRESS: Check for existing session.
          // getCurrentSession THROWS when the backend is unreachable (network / 5xx),
          // so retry with backoff (~26s) to ride out a deploy window instead of
          // treating the outage as "logged out" and burning a delegation code.
          const currentSession = await withRetry(() => authClient.getCurrentSession(), {
            maxAttempts: 6,
            baseDelayMs: 1500,
            maxDelayMs: 8000,
          });
          if (mounted) {
            setSession(currentSession);
            setLoading(false);
            setReady(true); // No consume needed, ready immediately

            if (currentSession.authenticated) {
              console.log('Active session restored');
            } else {
              console.log('No active session found');
            }
          }
        }
      } catch (err) {
        // Unexpected error
        console.error('Authentication initialization error:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Authentication failed'));
          setSession({ authenticated: false });
          setLoading(false);
          setReady(true); // Even on error, we're "ready" (just in error state)
        }
      }
    }

    initAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setError(null);
      await authClient.logout();
      setSession({ authenticated: false });
      console.log('Logged out successfully');
    } catch (err) {
      console.error('Logout error:', err);
      setError(err instanceof Error ? err : new Error('Logout failed'));
      throw err;
    }
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      setError(null);
      const currentSession = await authClient.getCurrentSession();
      setSession(currentSession);

      if (!currentSession.authenticated) {
        console.log('Session expired or invalid');
      }
    } catch (err) {
      console.error('Session refresh error:', err);
      setError(err instanceof Error ? err : new Error('Session refresh failed'));
      setSession({ authenticated: false });
    }
  }, []);

  return {
    session,
    loading,
    ready,
    error,
    logout,
    refreshSession,
  };
}
